<template>
  <transition name="slide-and-appear">
    <div v-if="isVisible" class="slide-in-survey" @click="reportClickToDy">
      <form
        class="slide-in-survey__form"
        :class="{ 'slide-in-survey__form--transparent': isShowingSuccessMessage }"
        novalidate
        @submit.prevent="onSubmit"
      >
        <span class="slide-in-survey__close icon-cross" @click="hide"></span>
        <div class="slide-in-survey__question" v-html="renderQuestion(question.text)"></div>
        <div class="slide-in-survey__fields">
          <OptionRadio
            v-for="(answer, index) in answers"
            :key="`survey-answer-${index}`"
            :id="answer.value"
            :selectedId.sync="selectedAnswerId"
            name="survey-answers"
            :isDisabled="isSubmitting"
          >
            <ListItem :title="answer.text" />
          </OptionRadio>
          <div class="slide-in-survey__button-wrap">
            <Button type="submit" :isDisabled="!selectedAnswer" :isSubmitting="isSubmitting">
              {{ buttonText }}
            </Button>
          </div>
        </div>
      </form>
      <div
        class="slide-in-survey__success"
        :class="{ 'slide-in-survey__success--hidden': !isShowingSuccessMessage }"
      >
        <div class="slide-in-survey__success-text">
          {{ successMessage }}
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import SlideInSurveyService from '@/api/services/slide-in-survey-service'
import { onFontsReady } from '@/helpers/event'
import { resolveLineBreakOfLastWord } from '@/helpers/string'
import { setCookie, getCookie } from '@/helpers/cookies'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import OptionRadio from '@/components/_shared/Form/Radio/OptionRadio.vue'
import ListItem from '@/components/_shared/ListItem.vue'
import Button from '@/components/_shared/Button.vue'

const MIN_DELAY_BEFORE_SHOWING = 1000
const FAKE_SUBMIT_TIME = 500
const SUCCESS_MSG_DISPLAY_TIME = 1500
const COOKIE_HIDDEN_VALUE = 'hidden'

export default {
  name: 'SlideInSurvey',
  components: {
    OptionRadio,
    ListItem,
    Button,
  },

  data() {
    return {
      answers: [],
      buttonText: '',
      delayBeforeShowing: 0,
      campaignIds: {},
      isShowingSuccessMessage: false,
      isSubmitting: false,
      isVisible: false,
      question: {},
      selectedAnswerId: '',
      successMessage: '',
      timeoutToShow: null,
    }
  },

  computed: {
    hasValidContent() {
      const hasTextAndValue = obj => obj && obj.text && obj.value
      return (
        hasTextAndValue(this.question) &&
        this.answers.length > 0 &&
        this.answers.every(answer => hasTextAndValue(answer)) &&
        this.buttonText &&
        this.successMessage
      )
    },

    selectedAnswer() {
      const answer = this.answers.find(answer => answer.value === this.selectedAnswerId)
      return answer ? answer.value : undefined
    },

    cookieName() {
      return this.hasValidContent ? `dy_survey_${this.parameterize(this.question.value)}` : ''
    },

    hasBeenClosedInCookie() {
      return this.cookieName ? getCookie(this.cookieName) == COOKIE_HIDDEN_VALUE : false
    },
  },

  watch: {
    hasValidContent() {
      clearTimeout(this.timeoutToShow)
      if (!this.hasBeenClosedInCookie && this.hasValidContent) {
        const delay = Math.max(this.delayBeforeShowing, MIN_DELAY_BEFORE_SHOWING)
        this.timeoutToShow = setTimeout(async () => {
          await onFontsReady()
          this.isVisible = true
        }, delay)
      }
    },
  },

  created() {
    this.fetchSurvey()
  },

  methods: {
    async fetchSurvey() {
      const survey = await SlideInSurveyService.fetchSurvey()
      const { answers, buttonText, campaignIds, question, successMessage } = survey
      Object.assign(this, { answers, buttonText, campaignIds, question, successMessage })

      this.delayBeforeShowing = (survey.delay || 0) * 1000
    },

    renderQuestion(question) {
      // Especially useful in French since there is always a space before a question mark
      return resolveLineBreakOfLastWord(question, {
        maxLengthOfLastWord: 5,
      })
    },

    hide() {
      clearTimeout(this.timeoutToShow)
      this.isVisible = false
      setCookie(this.cookieName, COOKIE_HIDDEN_VALUE)
    },

    parameterize(str) {
      return str
        .toLowerCase()
        .replace(/[^a-z]+/g, '_')
        .replace(/(^_)?(.+[a-z])(_$)?/, '$2')
    },

    reportClickToDy() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignClick,
        campaignIds: this.campaignIds,
      })
    },

    onSubmit() {
      this.isSubmitting = true

      notifyExternalServices({
        action: ACTIONS.survey,
        details: { action: this.question.value, label: this.selectedAnswer },
      })
      setTimeout(() => {
        this.isShowingSuccessMessage = true
        setTimeout(this.hide, SUCCESS_MSG_DISPLAY_TIME)
      }, FAKE_SUBMIT_TIME)
    },
  },
}
</script>

<style lang="scss" scoped>
$box-width: 320px;
$box-padding-x: 24px;
$box-padding-y: 20px;
$question-font-size: $font-size-small;
$question-line-height: 1.5;
$question-line-height-px: $question-font-size * $question-line-height;
$question-line-space-top: round(($question-line-height-px - $question-font-size) / 2);
$close-icon-size: 12px;

$form-fade-out-time: 0.1s;

.slide-in-survey {
  position: fixed;
  overflow: hidden;
  bottom: 0;
  right: calc(50% - #{round($box-width / 2)}); // not using transform to avoid animation conflicts
  background-color: $color-white;
  border-radius: 1px;
  width: $box-width;
  max-width: $box-width;
  box-shadow: 0 10px 32px 0 rgba(0, 0, 0, 0.15);
  font-family: $font-family-helvetica-now;
  z-index: $navigation-z-index - 1;

  @include min-screen($mq-width-medium) {
    right: 40px;
  }

  @include min-screen($mq-width-high) {
    right: 100px;
  }

  &__form {
    position: relative;
    padding: $box-padding-y $box-padding-x;
    transition: opacity $form-fade-out-time ease-in-out;

    &--transparent {
      opacity: 0;
    }
  }

  &__close {
    position: absolute;
    top: 1px;
    right: 0;
    padding: $box-padding-y $box-padding-x;
    margin-right: 0;
    line-height: $question-line-height;
    font-size: $close-icon-size;
    cursor: pointer;
    z-index: 5;

    &:hover {
      opacity: 0.6;
    }
  }

  &__question {
    line-height: $question-line-height;
    font-size: $question-font-size;
    font-weight: bold;
    padding-right: $close-icon-size + $box-padding-x - 5;
  }

  &__fields,
  &__button-wrap {
    margin-top: $box-padding-y;
  }

  &__success {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    top: 0;
    left: 0;
    padding: $box-padding-y $box-padding-x;
    width: 100%;
    height: 100%;
    transition: opacity 0.3s ease-in-out $form-fade-out-time;

    &--hidden {
      opacity: 0;
      transform: translateX(100%);
    }
  }

  &__success-text {
    margin: auto;
    font-size: $font-size-larger;
  }
}

.slide-and-appear-enter-active {
  transition: opacity 0.5s ease-in-out, transform 0.5s $transition-cubic;
}

.slide-and-appear-leave-active {
  transition: opacity 0.4s ease-in-out;

  & .slide-in-survey__success {
    transition: opacity 0.2s;
  }
}

.slide-and-appear-enter {
  opacity: 0;
  transform: translate(0, 50px);
}

.slide-and-appear-leave-to {
  opacity: 0;

  & .slide-in-survey__success {
    opacity: 0;
  }
}
</style>
